import React from "react";
import {
    Box,
    Typography,
    Paper,
    Stack,
    Divider,
    Link,
} from "@mui/material";

const citationsData = [
    {
        tool: "ResFinder",
        title: "ResFinder 4.0 for predictions of phenotypes from genotypes.",
        authors:
            "Bortolaia V, Kaas RF, Ruppe E, Roberts MC, Schwarz S, Cattoir V, Philippon A, Allesoe RL, Rebelo AR, Florensa AR, Fagelhauer L, Chakraborty T, Neumann B, Werner G, Bender JK, Stingl K, Nguyen M, Coppens J, Xavier BB, Malhotra-Kumar S, Westh H, Pinholt M, Anjum MF, Duggett NA, Kempf I, Nykäsenoja S, Olkkola S, Wieczorek K, Amaro A, Clemente L, Mossong J, Losch S, Ragimbeau C, Lund O, Aarestrup FM.",
        journal: "Journal of Antimicrobial Chemotherapy.",
        published: "2020 Aug 11",
        pmid: "32780112",
        doi: "10.1093/jac/dkaa345",
    },
    {
        tool: "RGI",
        title:
            "CARD 2023: expanded curation, support for machine learning, and resistome prediction at the Comprehensive Antibiotic Resistance Database.",
        authors: "Alcock et al.",
        journal: "Nucleic Acids Research",
        year: "2023",
        volumeIssuePages: "51, D690-D699",
        pmid: "36263822",
    },
    {
        tool: "VirulenceFinder",
        title:
            "Real-time whole-genome sequencing for routine typing, surveillance, and outbreak detection of verotoxigenic Escherichia coli.",
        authors:
            "Joensen, K. G., Scheutz, F., Lund, O., Hasman, H., Kaas, R. S., Nielsen, E. M., & Aarestrup, F. M.",
        journal: "Journal of Clinical Microbiology",
        year: "2014",
        volumeIssuePages: "52(5), 1501–1510",
        doi: "10.1128/JCM.03617-13",
    },
    {
        tool: "MLST",
        title:
            "The mlst software incorporates components of the PubMLST database and related citation requirements.",
        authors: "Seemann T.; Jolley K. and the PubMLST resource",
        journal: "PubMLST / Wellcome Open Research",
        year: "2018",
        volumeIssuePages: "3:124",
        extraText: [
            "This publication made use of the PubMLST website (https://pubmlst.org/) developed by Keith Jolley, Wellcome Open Research. 2018 Sep 24; 3:124, and sited at the University of Oxford. The development of that website was funded by the Wellcome Trust.",
            "You should also cite this software (currently unpublished) as: Seemann T, mlst GitHub https://github.com/tseemann/mlst.",
            "LICENSE: Use or reproduction of these materials, in whole or in part, by any commercial organization, whether for non-commercial (including research) or commercial purposes, is prohibited.",
        ],
    },
    {
        tool: "Kaptive",
        title: "Fast and Accurate in silico Antigen Typing with Kaptive 3.",
        authors:
            "Stanton TD, Hetland MAK, Löhr IH, Holt KE, Wyres KL.",
        journal: "Microbial Genomics",
        year: "2025",
        volumeIssuePages: "11(6):001428",
        doi: "10.1099/mgen.0.001428",
        extraText: [
            "If you use the Klebsiella K or O locus databases, please cite:",
            "Wyres KL, Wick RR, Gorrie C, Jenney A, Follador R, Thomson NR, Holt KE. Identification of Klebsiella capsule synthesis loci from whole genome data. Microbial Genomics 2, e000102. https://doi.org/10.1099/mgen.0.000102",
            "Lam MMC, Wick RR, Judd LM, Holt KE, Wyres KL. Kaptive 2.0: updated capsule and lipopolysaccharide locus typing for the Klebsiella pneumoniae species complex. Microbial Genomics 8. https://doi.org/10.1099/mgen.0.000800",

            "If you use the A. baumannii K or OC locus database(s), please cite:",
            "Wyres KL, Cahill SM, Holt KE, Hall RM, Kenyon JJ. Identification of Acinetobacter baumannii loci for capsular polysaccharide (KL) and lipooligosaccharide outer core (OCL) synthesis. Microbial Genomics 6. https://doi.org/10.1099/mgen.0.000339",
            "Cahill SM, Hall RM, Kenyon JJ. Update to the database for Acinetobacter baumannii capsular polysaccharide locus typing. Microbial Genomics 8. https://doi.org/10.1099/mgen.0.000878",
        ],
    }
];

function getPalette(mode) {
    const isDark = mode === "dark";

    return {
        outerBg: isDark ? "transparent" : "transparent",
        containerBg: isDark ? "#000" : "#FFFFFF",
        containerColor: isDark ? "#fff" : "#111827",
        containerBorder: isDark
            ? "1px solid rgba(255,255,255,0.08)"
            : "1px solid rgba(17,24,39,0.08)",
        cardBg: isDark ? "#111" : "#F9FAFB",
        cardBorder: isDark
            ? "1px solid rgba(255,255,255,0.12)"
            : "1px solid rgba(17,24,39,0.10)",
        titleColor: isDark ? "#fff" : "#111827",
        subtitleColor: isDark ? "rgba(255,255,255,0.75)" : "rgba(17,24,39,0.72)",
        introColor: isDark ? "rgba(255,255,255,0.8)" : "rgba(17,24,39,0.78)",
        dividerColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(17,24,39,0.10)",
        toolColor: "#20BC71",
        mainText: isDark ? "#fff" : "#111827",
        secondaryText: isDark ? "rgba(255,255,255,0.82)" : "rgba(17,24,39,0.82)",
        tertiaryText: isDark ? "rgba(255,255,255,0.78)" : "rgba(17,24,39,0.76)",
        metaText: isDark ? "rgba(255,255,255,0.72)" : "rgba(17,24,39,0.70)",
        labelText: isDark ? "#fff" : "#111827",
        valueText: isDark ? "rgba(255,255,255,0.75)" : "rgba(17,24,39,0.75)",
        linkColor: isDark ? "#4CC99F" : "#0F9D58",
    };
}

function CitationItem({ item, palette }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                backgroundColor: palette.cardBg,
                border: palette.cardBorder,
            }}
        >
            <Stack spacing={1.25}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        color: palette.toolColor,
                        letterSpacing: 0.2,
                    }}
                >
                    {item.tool}
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        color: palette.mainText,
                        fontWeight: 600,
                        lineHeight: 1.6,
                    }}
                >
                    {item.title}
                </Typography>

                {item.authors && (
                    <Typography
                        variant="body2"
                        sx={{ color: palette.secondaryText, lineHeight: 1.8 }}
                    >
                        {item.authors}
                    </Typography>
                )}

                {Array.isArray(item.extraText) && item.extraText.length > 0 && (
                    <Stack spacing={1}>
                        {item.extraText.map((text, index) => (
                            <Typography
                                key={`${item.tool}-extra-${index}`}
                                variant="body2"
                                sx={{ color: palette.tertiaryText, lineHeight: 1.8 }}
                            >
                                {text}
                            </Typography>
                        ))}
                    </Stack>
                )}

                <Typography
                    variant="body2"
                    sx={{ color: palette.metaText, lineHeight: 1.8 }}
                >
                    {[
                        item.journal,
                        item.year && !item.published ? item.year : null,
                        item.published,
                        item.volumeIssuePages,
                    ]
                        .filter(Boolean)
                        .join(" ")}
                </Typography>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {item.pmid && (
                        <Typography
                            variant="body2"
                            sx={{ color: palette.valueText }}
                        >
                            <Box
                                component="span"
                                sx={{ fontWeight: 700, color: palette.labelText }}
                            >
                                PMID:
                            </Box>{" "}
                            {item.pmid}
                        </Typography>
                    )}

                    {item.doi && (
                        <Typography
                            variant="body2"
                            sx={{ color: palette.valueText }}
                        >
                            <Box
                                component="span"
                                sx={{ fontWeight: 700, color: palette.labelText }}
                            >
                                DOI:
                            </Box>{" "}
                            <Link
                                href={`https://doi.org/${item.doi}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                sx={{ color: palette.linkColor }}
                            >
                                {item.doi}
                            </Link>
                        </Typography>
                    )}
                </Box>
            </Stack>
        </Paper>
    );
}

export default function AnalyzeResultsCitations({
    title = "How to cite the analizys tool",
    subtitle = "References for the tools used during the analysis.",
    citations = citationsData,
    mode = "dark", // "dark" | "light"
}) {
    const palette = getPalette(mode);

    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                backgroundColor: palette.outerBg,
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    px: { xs: 2, md: 4 },
                    py: { xs: 3, md: 5 },
                    backgroundColor: palette.containerBg,
                    color: palette.containerColor,
                    borderRadius: 8,
                    border: palette.containerBorder,
                }}
            >
                <Box sx={{ maxWidth: 1100, mx: "auto" }}>
                    <Stack spacing={2.5}>
                        <Box>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 800,
                                    mb: 1,
                                    fontSize: { xs: "1.8rem", md: "2.2rem" },
                                    color: palette.titleColor,
                                }}
                            >
                                {title}
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    color: palette.subtitleColor,
                                    maxWidth: 800,
                                    lineHeight: 1.8,
                                }}
                            >
                                {subtitle}
                            </Typography>
                        </Box>

                        <Typography
                            variant="body2"
                            sx={{
                                color: palette.introColor,
                                fontStyle: "italic",
                                mb: 1,
                            }}
                        >
                            If you use the results generated by this platform, please
                            cite the source software accordingly:
                        </Typography>

                        <Divider sx={{ borderColor: palette.dividerColor }} />

                        <Stack spacing={2}>
                            {citations.map((item, index) => (
                                <CitationItem
                                    key={`${item.tool}-${index}`}
                                    item={item}
                                    palette={palette}
                                />
                            ))}
                        </Stack>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
}